"use client";

import type { FC } from "react";
import {
  Button,
  Flex,
  FormRow,
  Modal,
  type ModalProps,
  ScrollPane,
  Select,
  TabsNavigation,
  Text,
  TextField,
  type ThemeName,
  useLocalState,
  usePubSub,
} from "@huub-web/ui";
import { useNavigationState } from "../../providers";
import { useTemplates, type TemplateCollection } from "@huub-web/templating";
import {
  useAuth,
  WebSocketChannel,
  type WebSocketSuggestionsMessage,
  type WebSocketChatTemplatesMessage,
} from "@huub-web/backend";
import { generateUuid } from "@huub-web/utils";

import { useAppController } from "../../hooks/useAppController";
import { useAppSettings } from "../../hooks/useAppSettings";
import { FUNNEL_DATA_KEY, type FunnelData } from "../../types/FunnelData";

export type DebugModalProps = ModalProps;

export const DebugModal: FC<DebugModalProps> = ({ onClose, ...props }) => {
  const { agentResponses, navigationState, appCustomer, conversation } =
    useAppController();
  const { navigationContext, navigationalPathMap } = useNavigationState();
  const { isVisualDebugEnabled, isAudioEnabled, theme, setTheme } =
    useAppSettings();
  const { dataFieldValues } = useTemplates();
  const { indentity, identityToken } = useAuth();
  const pubSub = usePubSub();

  const handleMockSuggestions = () => {
    const id = generateUuid();
    const message: WebSocketSuggestionsMessage = {
      type: WebSocketChannel.SUGGESTIONS,
      data: {
        id,
        data: {
          suggestions: [
            "Ik wil weten wat ik kan lenen",
            "Ik wil uitleg over verschillende hypotheekvormen",
          ],
        },
      },
    };

    pubSub.publish("mockWebSocketMessage", {
      event: new MessageEvent("message", {
        data: JSON.stringify(message),
      }),
    });

    onClose?.();
  };

  const handleMockChatTemplates = () => {
    if (!conversation || conversation.entries.length === 0) {
      return;
    }

    const { id: thread_id } = conversation;
    const { id } = conversation.entries[conversation.entries.length - 1];

    const message: WebSocketChatTemplatesMessage<TemplateCollection> = {
      type: WebSocketChannel.CHAT_TEMPLATES,
      data: {
        message_id: id,
        thread_id: thread_id as string,
        data: {
          templates: [
            {
              id: generateUuid(),
              templateKey: "button",
              data: {
                value: "Schakel Huub in",
                type: "next",
                action: {
                  type: "navigate",
                  payload: {
                    context: ["assessment", "myDetails"],
                  },
                },
              },
            },
          ],
        },
      },
    };

    pubSub.publish("mockWebSocketMessage", {
      event: new MessageEvent("message", {
        data: JSON.stringify(message),
      }),
    });

    onClose?.();
  };

  const [funnelData] = useLocalState<FunnelData>(FUNNEL_DATA_KEY, {
    __followUp: false,
  });

  const handleMockTemplatesDynamicTitle = () => {
    pubSub.publish("titleUpdate", {
      title: "Dynamic Title",
    });
    onClose?.();
  };

  const handleMockChatSimulation = () => {
    const messageID = generateUuid();
    const threadID = generateUuid();

    onClose?.();

    // First message - start streaming with context
    pubSub.publish("mockWebSocketMessage", {
      event: new MessageEvent("message", {
        data: JSON.stringify({
          type: WebSocketChannel.CHAT,
          data: {
            message_id: messageID,
            thread_id: threadID,
            context: ["hub"],
            message: "Kun je mij in het ",
            streaming: true,
            interaction: true,
            interaction_label: "Simulatie",
          },
        }),
      }),
    });

    // Second message - continue streaming
    setTimeout(() => {
      pubSub.publish("mockWebSocketMessage", {
        event: new MessageEvent("message", {
          data: JSON.stringify({
            type: WebSocketChannel.CHAT,
            data: {
              message_id: messageID,
              thread_id: threadID,
              message: "kort iets over jezelf",
              streaming: true,
            },
          }),
        }),
      });
    }, 500);

    // Final message - complete message with streaming false
    setTimeout(() => {
      pubSub.publish("mockWebSocketMessage", {
        event: new MessageEvent("message", {
          data: JSON.stringify({
            type: WebSocketChannel.CHAT,
            data: {
              message_id: messageID,
              thread_id: threadID,
              message: "vertellen?",
              streaming: true,
            },
          }),
        }),
      });

      pubSub.publish("mockWebSocketMessage", {
        event: new MessageEvent("message", {
          data: JSON.stringify({
            type: WebSocketChannel.CHAT,
            data: {
              message_id: messageID,
              thread_id: threadID,
              message: "Kun je mij in het kort iets over jezelf vertellen?",
              streaming: false,
            },
          }),
        }),
      });
    }, 1000);

    setTimeout(() => {
      pubSub.publish("mockWebSocketMessage", {
        event: new MessageEvent("message", {
          data: JSON.stringify({
            type: WebSocketChannel.CHAT_FEEDBACK,
            data: {
              message_id: messageID,
              thread_id: threadID,
              run_id: generateUuid(),
              feedback_options: {
                correctness: true,
                tags: true,
                comments: true,
                available_tags: {
                  COMPLIANCE: "Compliance",
                  CONTENT: "Content",
                  BUSINESS_FLOW: "Business flow",
                  TONE_OF_VOICE: "Tone of voice",
                  DATA: "Data",
                },
              },
            },
          }),
        }),
      });
    }, 1500);
  };

  return (
    <Modal
      title="Debug"
      placement="top"
      size="medium"
      onClose={onClose}
      closeable
      {...props}
    >
      <TabsNavigation
        variant="outlined"
        color="background"
        overflowHidden
        tabs={[
          {
            id: "navigationState",
            label: "Navigation State",
            content: (
              <TabsNavigation
                variant="outlined"
                color="background"
                tabs={[
                  {
                    id: "navigationState",
                    label: "Navigation State",
                    content: (
                      <Flex direction="column" gap="s">
                        <Text bold>Navigation Context</Text>
                        <CodeBlock>
                          {JSON.stringify(navigationContext, null, 2)}
                        </CodeBlock>
                        <Text bold>Navigation State</Text>
                        <CodeBlock>
                          {JSON.stringify(navigationState, null, 2)}
                        </CodeBlock>
                      </Flex>
                    ),
                  },
                  {
                    id: "appCustomer",
                    label: "Customer",
                    content: (
                      <Flex>
                        <CodeBlock>
                          {JSON.stringify(appCustomer, null, 2)}
                        </CodeBlock>
                      </Flex>
                    ),
                  },
                  {
                    id: "appNavigationState",
                    label: "Navigation Path Map",
                    content: (
                      <Flex gap="s" direction="column">
                        <CodeBlock>
                          {JSON.stringify(navigationalPathMap, null, 2)}
                        </CodeBlock>
                      </Flex>
                    ),
                  },
                  // {
                  //   id: "appCurrent",
                  //   label: "Current",
                  //   content: (
                  //     <Flex direction="column" gap="s">
                  //       <Message variant="warning">Deprecated.</Message>
                  //       <Flex gap="s" align="center">
                  //         <Text>Set current phase to</Text>
                  //         <Group>
                  //           <Button
                  //             size="s"
                  //             border="default"
                  //             disabled
                  //             // onClick={() => {
                  //             //   pubSub.publish("currentPhaseUpdate", {
                  //             //     phase: "introduction",
                  //             //   });
                  //             // }}
                  //           >
                  //             introduction
                  //           </Button>
                  //           <Button
                  //             size="s"
                  //             border="default"
                  //             disabled
                  //             // onClick={() => {
                  //             //   pubSub.publish("currentPhaseUpdate", {
                  //             //     phase: "orientation",
                  //             //   });
                  //             // }}
                  //           >
                  //             orientation
                  //           </Button>
                  //         </Group>
                  //       </Flex>
                  //       <CodeBlock>
                  //         {JSON.stringify(appCurrent, null, 2)}
                  //       </CodeBlock>
                  //     </Flex>
                  //   ),
                  // },
                ]}
              />
            ),
          },
          {
            id: "indentity",
            label: "oAuth",
            content: (
              <Flex direction="column" gap="s">
                <Text bold>Identity Token</Text>
                <TextField value={identityToken ?? ""} />
                <Text bold>Identity</Text>
                <CodeBlock>{JSON.stringify(indentity, null, 2)}</CodeBlock>
              </Flex>
            ),
          },
          {
            id: "websocket",
            label: "WebSocket & API",
            content: (
              <Flex direction="column" gap="m">
                <Flex gap="xs" direction="column" align="flex-start">
                  <Flex gap="xs" align="flex-start">
                    <Button
                      size="s"
                      border="default"
                      variant="opaque"
                      onClick={handleMockSuggestions}
                    >
                      <Text color="currentColor50" variant="bodyXS">
                        Test
                      </Text>
                      WebSocket Suggestions
                    </Button>
                    <Button
                      size="s"
                      border="default"
                      variant="opaque"
                      onClick={() => {
                        pubSub.publish("apiRequest", {
                          method: "GET",
                          endpoint: "/api/lorem-ipsum",
                        });
                        onClose?.();
                      }}
                    >
                      <Text color="currentColor50" variant="bodyXS">
                        Test
                      </Text>
                      API Request Action
                    </Button>
                  </Flex>
                  <Flex gap="xs" align="flex-start">
                    <Button
                      size="s"
                      variant="opaque"
                      border="default"
                      onClick={handleMockChatTemplates}
                    >
                      <Text color="currentColor50" variant="bodyXS">
                        Test
                      </Text>
                      WebSocket Chat Templates
                    </Button>
                    <Button
                      size="s"
                      variant="opaque"
                      border="default"
                      onClick={handleMockTemplatesDynamicTitle}
                    >
                      <Text color="currentColor50" variant="bodyXS">
                        Test
                      </Text>
                      Templates dynamic title
                    </Button>
                  </Flex>
                  <Flex gap="xs" align="flex-start">
                    <Button
                      size="s"
                      variant="opaque"
                      border="default"
                      onClick={handleMockChatSimulation}
                    >
                      <Text color="currentColor50" variant="bodyXS">
                        Test (only works on hub)
                      </Text>
                      WebSocket Chat Simulation
                    </Button>
                  </Flex>
                </Flex>
                <Flex direction="column" gap="s">
                  <TabsNavigation
                    variant="outlined"
                    color="background"
                    tabs={[
                      {
                        id: "conversation",
                        label: "Conversation",
                        content: (
                          <CodeBlock>
                            {JSON.stringify(conversation, null, 2)}
                          </CodeBlock>
                        ),
                      },
                      {
                        id: "agentResponses",
                        label: "Agent Responses",
                        content: (
                          <CodeBlock>
                            {JSON.stringify(agentResponses, null, 2)}
                          </CodeBlock>
                        ),
                      },
                    ]}
                  />
                </Flex>
              </Flex>
            ),
          },
          {
            id: "appSettings",
            label: "App Settings",
            content: (
              <Flex direction="column" gap="m">
                <FormRow label="Theme" variant="auto">
                  <Select
                    value={theme}
                    onChange={(value) => {
                      const newTheme = value as ThemeName;
                      if (newTheme !== theme) {
                        setTheme(newTheme);
                        setTimeout(() => window.location.reload(), 500);
                      }
                    }}
                    options={[
                      { label: "Light", value: "light" },
                      { label: "Dark", value: "dark" },
                      { label: "Unive", value: "unive" },
                      { label: "De Hypotheker", value: "deHypotheker" },
                      { label: "ING", value: "ing" },
                      { label: "Rabobank", value: "rabobank" },
                      { label: "Quion", value: "quion" },
                      {
                        label: "Nationale Nederlanden",
                        value: "nationaleNederlanden",
                      },
                      {
                        label: "Van Lanschot Kempen",
                        value: "vanLanschotKempen",
                      },
                      {
                        label: "Vereniging Eigen Huis",
                        value: "verenigingEigenHuis",
                      },
                      {
                        label: "NIBC",
                        value: "nibc",
                      },
                      {
                        label: "Munt",
                        value: "munt",
                      },
                      {
                        label: "Hypotheekvisie",
                        value: "hypotheekvisie",
                      },
                      {
                        label: "Obvion",
                        value: "obvion",
                      },
                      {
                        label: "Vista",
                        value: "vista",
                      },
                      {
                        label: "a.s.r.",
                        value: "asr",
                      },
                      {
                        label: "ANWB",
                        value: "anwb",
                      },
                      {
                        label: "Evi van Lanschot",
                        value: "eviVanLanschot",
                      },
                      {
                        label: "Allianz",
                        value: "allianz",
                      },
                      {
                        label: "FNV",
                        value: "fnv",
                      },
                      {
                        label: "Randstad",
                        value: "randstad",
                      },
                      {
                        label: "Achmea",
                        value: "achmea",
                      },
                      {
                        label: "Funda",
                        value: "funda",
                      },
                      {
                        label: "NVM",
                        value: "nvm",
                      },
                      {
                        label: "ABN AMRO",
                        value: "abnamro",
                      },
                      {
                        label: "Domek.nl",
                        value: "domek",
                      },
                    ]}
                  />
                </FormRow>

                <CodeBlock>
                  {JSON.stringify(
                    {
                      isVisualDebugEnabled,
                      isAudioEnabled,
                      theme,
                    },
                    null,
                    2,
                  )}
                </CodeBlock>
              </Flex>
            ),
          },
          {
            id: "misc",
            label: "Miscellaneous",
            content: (
              <Flex direction="column" gap="s">
                <Text bold>Data field values</Text>
                <CodeBlock>
                  {JSON.stringify(dataFieldValues, null, 2)}
                </CodeBlock>
                <Text bold>Funnel data</Text>
                <CodeBlock>
                  {JSON.stringify(funnelData ?? {}, null, 2)}
                </CodeBlock>
              </Flex>
            ),
          },
        ]}
      />
    </Modal>
  );
};

const CodeBlock: FC<{ children: string }> = ({ children }) => {
  return (
    <ScrollPane style={{ maxHeight: "60vh", width: "100%" }}>
      <pre>{children}</pre>
    </ScrollPane>
  );
};
